import React from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {
    GridContainer,
    CenteredGrid,
    AnimatedHeading,
    VerticalSpacer,
    Typography,
    HiddenSSR,
    SubnavSectionConfigProps,
    SubnavSectionName,
    CmsButtonProps,
    Button
} from '..';
import {AnimateOnViewWrap} from '../../wraps/animation';
import {TextCard, TextCardProps} from '../cards/TextCard';

const Styled = styled.div``;

export interface TextWithLinksProps {
    accentColor?: AccentColorOptions;
    cards?: TextCardProps[];
    description?: string;
    heading?: string;
    button?: CmsButtonProps;
    componentMapperIndex?: number;
    sectionConfig?: SubnavSectionConfigProps;
}

export const TextWithLinks = (props: TextWithLinksProps): JSX.Element => {
    const {
        accentColor,
        cards = [],
        description,
        heading = '',
        button,
        componentMapperIndex,
        sectionConfig = {} as SubnavSectionConfigProps
    } = props;
    const isMd = useBreakpointDown('md');
    const isSm = useBreakpointDown('sm');
    const direction = isSm ? 'column' : 'row';
    const alignItems = isSm ? 'center' : 'stretch';
    const justifyCards = isMd ? 'flex-start' : 'flex-end';
    const wrapCards = isSm ? 'wrap' : 'nowrap';

    const noVerticalPadding = {paddingTop: 0, paddingBottom: 0};
    const noVerticalMargin = {marginTop: 0, marginBottom: 0};
    let textCard: TextCardProps;

    return (
        <Styled>
            <GridContainer>
                {componentMapperIndex === 0 && (
                    <Grid container>
                        <Grid item xs={12}>
                            <SubnavSectionName {...sectionConfig} />
                        </Grid>
                    </Grid>
                )}
                <CenteredGrid
                    direction={direction}
                    justifyContent={'flex-start'}
                    alignItems={'stretch'}
                    style={noVerticalMargin}>
                    <Grid item sm={12} md={4} style={noVerticalPadding}>
                        <div className="heading-text">
                            {heading && <AnimatedHeading variant="h2" heading={heading} />}
                        </div>
                        {description && (
                            <div className="description">
                                <AnimateOnViewWrap translateY={50}>
                                    <VerticalSpacer variant="custom" spacing={2} />
                                    <Typography variant="p">{description}</Typography>
                                </AnimateOnViewWrap>
                            </div>
                        )}
                        {button && button.label && (
                            <>
                                <VerticalSpacer variant="custom" spacing={4} />
                                <Button type={button.type} href={button.href} color={accentColor}>
                                    {button.label}
                                </Button>
                            </>
                        )}
                        <HiddenSSR lgUp>
                            <VerticalSpacer variant="custom" spacing={3} downSmSpacing={5} />
                        </HiddenSSR>
                    </Grid>

                    {cards.length ? (
                        <Grid
                            item
                            xs={12}
                            md={8}
                            className="section-intro-cards"
                            style={noVerticalPadding}>
                            <CenteredGrid
                                justifyContent={justifyCards}
                                alignItems={alignItems}
                                wrap={wrapCards}
                                style={noVerticalMargin}>
                                {cards.map((cardProps, index) => {
                                    return (
                                        <React.Fragment key={`resources-card-outer-${index}`}>
                                            {!isSm ? <Grid item md={2} /> : ''}
                                            <Grid item xs={12} md={10} style={noVerticalPadding}>
                                                <AnimateOnViewWrap
                                                    translateY={75}
                                                    delay={0.3 * (index + 1)}>
                                                    <TextCard
                                                        {...cardProps}
                                                        accentColor={accentColor}
                                                        {...textCard}
                                                        disableAnim
                                                    />
                                                </AnimateOnViewWrap>
                                                {index === 0 && (
                                                    <VerticalSpacer variant="custom" spacing={7} />
                                                )}
                                            </Grid>
                                        </React.Fragment>
                                    );
                                })}
                            </CenteredGrid>
                            <VerticalSpacer variant="custom" spacing={5} />
                        </Grid>
                    ) : (
                        ''
                    )}
                </CenteredGrid>
            </GridContainer>
        </Styled>
    );
};
